import { COMPARISON_OPERATORS, CONDITION_OPERATORS, PRESENCE_OPERATORS } from "./event-conditions";
import {
  workflowTriggerConditionOperators,
  WORKFLOW_TRIGGER_CATALOG,
} from "./workflow-trigger-catalog";
import { isTagResourceType, type TagResourceType } from "./tags";
import type { ActionType, ConditionType, TriggerType } from "./workflow-node-types";

/**
 * Domain model for the visual workflow graph.
 *
 * Persisted shape — what gets written to `workflow_definition_versions.graph_json`
 * and loaded back into the editor. This is distinct from the React Flow runtime
 * representation, which layers on synthetic branch-split nodes for visual purposes.
 */

export type WorkflowNodeKind = "trigger" | "condition" | "action";

export type TriggerConditionConnector = "and" | "or";

export type TriggerConditionClause = {
  id: string;
  field: string;
  operator: string;
  value: unknown;
  /** Connector to the previous clause. Ignored for the first clause. */
  connector: TriggerConditionConnector;
};

/**
 * Canonical catalog of the condition fields available on an `email_received`
 * trigger and the operators each one supports. Single source of truth shared by
 * the editor's field picker (`apps/web` predicate-clause), the matcher
 * (`packages/agents/src/email-trigger-match.ts`) and an agent's `email.received`
 * listener conditions. The picker derives its operator
 * lists from this map at compile time; a contract test in
 * `apps/worker/test/email-messages.test.ts` asserts the matcher implements every
 * field/operator listed here — so the two surfaces cannot silently drift.
 */
export const EMAIL_TRIGGER_CONDITION_FIELDS = workflowTriggerConditionOperators("email_received");

export type EmailTriggerConditionField =
  keyof (typeof WORKFLOW_TRIGGER_CATALOG)["email_received"]["conditionFields"];

/** Shared with `task.updated`, so a workflow and a listener filter status the same way. */
export const TASK_UPDATED_TRIGGER_CONDITION_FIELDS =
  workflowTriggerConditionOperators("task_updated");

export type TaskUpdatedTriggerConditionField =
  keyof (typeof WORKFLOW_TRIGGER_CATALOG)["task_updated"]["conditionFields"];

export type TriggerNode = {
  id: string;
  kind: "trigger";
  type: TriggerType;
  name: string;
  config: Record<string, unknown>;
  conditions: TriggerConditionClause[];
  position: { x: number; y: number };
};

export type ConditionNode = {
  id: string;
  kind: "condition";
  type: ConditionType;
  name: string;
  config: Record<string, unknown>;
  position: { x: number; y: number };
};

export type ActionNode = {
  id: string;
  kind: "action";
  type: ActionType;
  name: string;
  config: Record<string, unknown>;
  position: { x: number; y: number };
};

/**
 * Typed source for an email trigger — which inbound mailbox the workflow
 * watches. Stored in {@link TriggerNode.config} under the `source` key.
 *
 * The `mailgun` variant identifies a push (Mailgun) mailbox by its `imb_*`
 * public id (the `addressId` field carries that mailbox id).
 * The `graph_mailbox` variant (Phase 2, Microsoft Graph) identifies one specific
 * watched poll mailbox by its `imb_*` public id (plus the parent integration's
 * `int_*` id for coarse attribution) — a Graph mailbox cannot be expressed as a
 * `To` string, which is why source is its own typed field rather than a
 * condition clause. A workflow watches exactly one mailbox.
 */
export type TriggerSource =
  | { kind: "mailgun"; addressId: string }
  | { kind: "graph_mailbox"; integrationId: string; mailboxId: string };

/**
 * Read the typed source off a trigger node's config, or `null` when no
 * well-formed source is present. A missing source preserves today's behavior
 * (the trigger matches regardless of which address the email arrived on); a
 * present-but-malformed source returns `null` so callers can reject it.
 */
export function readTriggerSource(trigger: Pick<TriggerNode, "config">): TriggerSource | null {
  const raw = trigger.config?.source;
  if (!raw || typeof raw !== "object") return null;
  const kind = (raw as { kind?: unknown }).kind;

  if (kind === "mailgun") {
    const addressId = (raw as { addressId?: unknown }).addressId;
    if (typeof addressId === "string" && addressId.length > 0) {
      return { kind: "mailgun", addressId };
    }
    return null;
  }

  if (kind === "graph_mailbox") {
    const integrationId = (raw as { integrationId?: unknown }).integrationId;
    const mailboxId = (raw as { mailboxId?: unknown }).mailboxId;
    if (
      typeof integrationId === "string" &&
      integrationId.length > 0 &&
      typeof mailboxId === "string" &&
      mailboxId.length > 0
    ) {
      return { kind: "graph_mailbox", integrationId, mailboxId };
    }
    return null;
  }

  return null;
}

/**
 * Typed config for a `schedule` trigger — the cron cadence that fires the
 * workflow. Stored in {@link TriggerNode.config} (the cron analogue of an
 * email trigger's `source`): the editor writes it from the trigger panel, and
 * the API derives the workflow's `schedules` row from it on publish so the
 * worker poller has something to claim. `input` is the optional object merged
 * into each scheduled run's `ctx.input`.
 */
export type ScheduleTriggerConfig = {
  cron: string;
  timezone: string;
  input: Record<string, unknown>;
};

/**
 * Read the schedule config off a trigger node, or `null` when it isn't a
 * well-formed schedule (missing/blank cron or timezone). Mirrors
 * {@link readTriggerSource}: a present-but-malformed config returns `null` so
 * callers can reject it. Only validates shape here — the cron string's
 * *semantics* (parseable expression, cadence floor) are checked by
 * `@quovy/scheduler` on the publish path, which is where the cron parser lives.
 */
export function readScheduleTrigger(
  trigger: Pick<TriggerNode, "config">,
): ScheduleTriggerConfig | null {
  const config = trigger.config;
  if (!config || typeof config !== "object") return null;

  const cron = (config as { cron?: unknown }).cron;
  const timezone = (config as { timezone?: unknown }).timezone;
  if (typeof cron !== "string" || cron.trim().length === 0) return null;
  if (typeof timezone !== "string" || timezone.trim().length === 0) return null;

  const rawInput = (config as { input?: unknown }).input;
  const input =
    rawInput && typeof rawInput === "object" && !Array.isArray(rawInput)
      ? (rawInput as Record<string, unknown>)
      : {};

  return { cron: cron.trim(), timezone: timezone.trim(), input };
}

/** Empty `tagIds` / `resourceTypes` mean the trigger is unconfigured; it fires on nothing. */
export type TagAppliedTriggerConfig = {
  event: TagTriggerEvent;
  tagIds: string[];
  resourceTypes: TagResourceType[];
};

export const TAG_TRIGGER_EVENTS = ["applied", "removed"] as const;
export type TagTriggerEvent = (typeof TAG_TRIGGER_EVENTS)[number];

/** Operators whose left side is a resource id and right side a tag ref; their
 *  config shape shares nothing with the text operators. */
export const TAG_PREDICATE_OPERATORS = ["has_tag", "not_has_tag"] as const;
export type TagPredicateOperator = (typeof TAG_PREDICATE_OPERATORS)[number];

/**
 * Every operator a workflow branch can use. The editor's list, the publish
 * check and the runner all read this, so none can offer or accept an operator
 * the others do not know.
 */
export const CONDITION_NODE_OPERATORS = [
  ...CONDITION_OPERATORS,
  ...COMPARISON_OPERATORS,
  ...PRESENCE_OPERATORS,
  ...TAG_PREDICATE_OPERATORS,
] as const;
export type ConditionNodeOperator = (typeof CONDITION_NODE_OPERATORS)[number];

export function isConditionNodeOperator(operator: unknown): operator is ConditionNodeOperator {
  return (
    typeof operator === "string" &&
    (CONDITION_NODE_OPERATORS as readonly string[]).includes(operator)
  );
}

export function isTagPredicateOperator(operator: unknown): operator is TagPredicateOperator {
  return (
    typeof operator === "string" &&
    (TAG_PREDICATE_OPERATORS as readonly string[]).includes(operator)
  );
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

/**
 * Read the tag config off a trigger node. Unlike {@link readScheduleTrigger}
 * every field has a meaningful default, so this never returns `null`.
 */
export function readTagAppliedTrigger(
  trigger: Pick<TriggerNode, "config">,
): TagAppliedTriggerConfig {
  const config = (trigger.config ?? {}) as Record<string, unknown>;
  const event = config.event === "removed" ? "removed" : "applied";
  return {
    event,
    tagIds: stringList(config.tagIds),
    resourceTypes: stringList(config.resourceTypes).filter(isTagResourceType),
  };
}

/** True when a tagging write matches the trigger's event, tag and type filters. */
export function tagAppliedTriggerMatches(
  config: TagAppliedTriggerConfig,
  change: { event: TagTriggerEvent; tagPublicId: string; resourceType: TagResourceType },
): boolean {
  if (config.event !== change.event) return false;
  if (!config.tagIds.includes(change.tagPublicId)) return false;
  if (!config.resourceTypes.includes(change.resourceType)) return false;
  return true;
}

export type WorkflowNode = TriggerNode | ConditionNode | ActionNode;

export type WorkflowEdge = {
  id: string;
  source: string;
  target: string;
};

export type WorkflowGraph = {
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
};

export const WORKFLOW_GRAPH_VERSION = 1 as const;

/**
 * Create an empty graph seeded with a single trigger node at the origin.
 * Used when opening the editor for a new workflow.
 */
export function createEmptyWorkflowGraph(): WorkflowGraph {
  return {
    nodes: [
      {
        id: "trigger",
        kind: "trigger",
        type: "manual",
        name: "Choose a trigger",
        config: {},
        conditions: [],
        position: { x: 0, y: 0 },
      },
    ],
    edges: [],
  };
}
